/**
 * Everything a desk does to one patient, in tabs.
 *
 * Contracts, the front desk, the clinics manager and the secretary each open a
 * patient and do some of the same things — correct the file, set the cover,
 * file a form, send them to a department, hand them their link, take their
 * money. The tabs a desk sees are the intersection of what it asked for and
 * what the session may do, so one component serves four dashboards without
 * any of them showing a button the server would refuse.
 */

import { useEffect, useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Icon, Modal, Spinner, Tabs } from '../../components/common';
import { receiptsApi, requestsApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { PERMISSIONS } from '../../domain/rbac';
import { PatientFileForm } from './PatientFileForm';
import { CoverageEditor } from './CoverageEditor';
import { AttachmentsPanel } from './AttachmentsPanel';
import { RequestComposer } from './RequestComposer';
import { RequestCard } from './RequestCard';
import { MedicalFileView } from './MedicalFileView';
import { PortalLinkPanel } from './PortalLinkPanel';
import { ReceiptDialog, ReceiptForm, ReceiptPreview, blankLine } from './ReceiptSheet';
import { formatDateTime, formatMoney } from './labels';

const P = PERMISSIONS;

const ALL_SECTIONS = ['visit', 'file', 'coverage', 'attachments', 'route', 'medical', 'portal', 'receipts'];

/**
 * A new visit: what the patient came for, where, with whom — and the receipt
 * that sends them there (QA report §2.1, §4.3).
 *
 * The step the registration screen was missing. The desk picks the service
 * type, the clinic and the doctor from dropdowns, the price fills itself in,
 * and issuing the receipt puts the patient in clinics reception's queue (and
 * the lab's, radiology's…) at once. The form stays for the next visit.
 */
function VisitSection({ patient, onIssued }) {
  const [issued, setIssued] = useState(null);
  const [round, setRound] = useState(0);
  if (issued) {
    return (
      <div className="space-y-4">
        <div className="visit-done">
          <span className="tile3d__face tone-emerald !h-12 !w-12 shrink-0" aria-hidden="true">
            <Icon name="check-check" size={22} className="tile3d__icon" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-white">صدر الإيصال {issued.number}</p>
            <p className="text-xs text-slate-300">
              {(issued.routedRequestIds || []).length
                ? 'وُجّه المريض للأقسام المختارة — سيظهر الآن عند استقبال العيادات.'
                : 'إيصال بدون توجيه لقسم.'}
            </p>
          </div>
          <Button
            variant="ghost"
            icon="plus"
            onClick={() => {
              setIssued(null);
              setRound((r) => r + 1);
            }}
          >
            زيارة أخرى
          </Button>
        </div>
        <ReceiptPreview receipt={issued} />
      </div>
    );
  }
  return (
    <ReceiptForm
      key={round}
      patient={patient}
      defaults={{ lines: [blankLine('checkup')], receiptType: 'cash' }}
      onIssued={(receipt) => {
        setIssued(receipt);
        onIssued?.(receipt);
      }}
    />
  );
}

/** The patient's requests, newest first, with money when the server sends it. */
function PatientRequests({ patientId, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const load = () =>
    requestsApi
      .list({ hospitalPatientId: patientId })
      .then((r) => {
        setRows([...r].reverse());
        setError(null);
      })
      .catch(setError);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, refreshKey]);
  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!rows) return <Spinner label="جارٍ تحميل الطلبات" variant="inline" />;
  if (rows.length === 0) return <EmptyState icon="list-todo" title="لم يُرسل المريض لأي قسم بعد" />;
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <RequestCard key={r.id} request={r} showPatient={false} showMoney={Boolean(r.quote)} />
      ))}
    </div>
  );
}

/** The desk's till for one patient: balances Accounts sent, and past receipts. */
function PatientReceipts({ patient }) {
  const [due, setDue] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [viewing, setViewing] = useState(null);
  // The full receipt, so a reprint carries its queue ticket (QA report §5.3);
  // the row already in hand is shown if that read fails.
  const openReceipt = async (rc) => setViewing(await receiptsApi.get(rc.id).catch(() => rc));

  const load = async () => {
    try {
      const [d, r] = await Promise.all([
        requestsApi.list({ queue: 'desk', hospitalPatientId: patient.id }),
        receiptsApi.list({ hospitalPatientId: patient.id }),
      ]);
      setDue(d);
      setReceipts(r);
      setError(null);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!due || !receipts) return <Spinner label="جارٍ تحميل المستحقات" variant="inline" />;

  const total = due.reduce((s, r) => s + Number(r.amountDue || 0), 0);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-bold text-amber-100">مستحقات أرسلتها الحسابات</h4>
          <Badge className="border-amber-500/40 bg-amber-500/15 text-amber-100">{formatMoney(total)}</Badge>
        </div>
        {due.length === 0 ? (
          <p className="text-sm text-slate-400">لا توجد مبالغ مستحقة على هذا المريض.</p>
        ) : (
          <>
            <ul className="mb-3 space-y-1 text-sm text-slate-200">
              {due.map((r) => (
                <li key={r.id} className="flex justify-between gap-2">
                  <span>
                    {r.departmentLabel}: {r.title}
                  </span>
                  <b>{formatMoney(r.amountDue)}</b>
                </li>
              ))}
            </ul>
            <Button icon="hand-coins" onClick={() => setDialog({ requests: due })}>
              تحصيل وإصدار إيصال
            </Button>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-bold text-white">إيصالات المريض</h4>
        <Button variant="subtle" icon="receipt" onClick={() => setDialog({ requests: [] })}>
          إيصال جديد
        </Button>
      </div>
      {receipts.length === 0 ? (
        <EmptyState icon="receipt" title="لا توجد إيصالات" />
      ) : (
        <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
          {receipts.map((rc) => (
            <li key={rc.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
              <span className="font-mono text-cyan-200" dir="ltr">
                {rc.number}
              </span>
              <span className="text-slate-400">{formatDateTime(rc.issuedAt)}</span>
              <span className="ms-auto font-bold text-white">{formatMoney(rc.total)}</span>
              <Button variant="subtle" icon="printer" onClick={() => openReceipt(rc)}>
                عرض / طباعة
              </Button>
            </li>
          ))}
        </ul>
      )}

      {dialog && (
        <ReceiptDialog
          open
          patient={patient}
          requests={dialog.requests}
          onClose={() => setDialog(null)}
          onIssued={load}
        />
      )}
      <Modal open={Boolean(viewing)} onClose={() => setViewing(null)} title={viewing?.number} icon="receipt" size="lg">
        {viewing && <ReceiptPreview receipt={viewing} />}
      </Modal>
    </div>
  );
}

export function PatientWorkspace({
  patient,
  onPatientChange,
  sections = ALL_SECTIONS,
  defaultSection = null,
  bookingId = null,
  requestDepartments,
}) {
  const { can, canAny } = usePermissions();
  const [refreshKey, setRefreshKey] = useState(0);

  const readers = [P.PATIENT_RECORD_VIEW, P.PATIENT_FILE_MANAGE, P.INSURANCE_MANAGE, P.SERVICE_REQUEST_APPROVE, P.BILLING_MANAGE];
  const allowed = {
    visit: can(P.RECEIPT_ISSUE),
    file: canAny(readers),
    coverage: canAny(readers) && (patient?.patientType === 'insured' || can(P.INSURANCE_MANAGE)),
    attachments: canAny(P.ATTACHMENT_MANAGE, ...readers),
    route: can(P.SERVICE_REQUEST_CREATE),
    medical: can(P.PATIENT_RECORD_VIEW),
    portal: can(P.SHARE_LINK_ISSUE),
    receipts: can(P.RECEIPT_ISSUE),
  };

  const META = {
    visit: { label: 'زيارة جديدة وإيصال', icon: 'ticket-plus' },
    file: { label: 'بيانات المريض', icon: 'user' },
    coverage: { label: 'التغطية التأمينية', icon: 'shield-plus' },
    attachments: { label: 'المرفقات', icon: 'paperclip' },
    route: { label: 'توجيه لقسم', icon: 'navigation' },
    medical: { label: 'الملف الطبي', icon: 'file-heart' },
    portal: { label: 'رابط المريض وQR', icon: 'qr-code' },
    receipts: { label: 'الإيصالات', icon: 'receipt' },
  };

  const visible = sections.filter((id) => allowed[id]);
  const [section, setSection] = useState(defaultSection && visible.includes(defaultSection) ? defaultSection : visible[0]);

  useEffect(() => {
    if (!visible.includes(section)) setSection(visible[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.join(',')]);

  if (!patient) {
    return (
      <PatientFileForm
        patient={null}
        bookingId={bookingId}
        onSaved={(saved) => onPatientChange?.(saved)}
        readOnly={!can(P.PATIENT_FILE_MANAGE)}
      />
    );
  }

  return (
    <div>
      <Tabs tabs={visible.map((id) => ({ id, ...META[id] }))} active={section} onChange={setSection} />

      {section === 'visit' && <VisitSection patient={patient} onIssued={() => setRefreshKey((k) => k + 1)} />}
      {section === 'file' && (
        <PatientFileForm
          patient={patient}
          bookingId={bookingId}
          onSaved={(saved) => onPatientChange?.(saved)}
          readOnly={!can(P.PATIENT_FILE_MANAGE)}
        />
      )}
      {section === 'coverage' && (
        <CoverageEditor
          patientId={patient.id}
          readOnly={!can(P.INSURANCE_MANAGE)}
          onSaved={() => patient.patientType !== 'insured' && onPatientChange?.({ ...patient, patientType: 'insured' })}
        />
      )}
      {section === 'attachments' && <AttachmentsPanel patientId={patient.id} canUpload={can(P.ATTACHMENT_MANAGE)} />}
      {section === 'route' && (
        <div className="space-y-6">
          <RequestComposer
            patient={patient}
            bookingId={bookingId}
            departments={requestDepartments}
            onCreated={() => setRefreshKey((k) => k + 1)}
          />
          <div>
            <h4 className="mb-3 text-sm font-bold text-white">طلبات المريض</h4>
            <PatientRequests patientId={patient.id} refreshKey={refreshKey} />
          </div>
        </div>
      )}
      {section === 'medical' && <MedicalFileView patientId={patient.id} bookingId={bookingId} />}
      {section === 'portal' && <PortalLinkPanel patient={patient} />}
      {section === 'receipts' && <PatientReceipts patient={patient} />}
    </div>
  );
}

export default PatientWorkspace;
